import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  Card,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { User } from '../../types';
import { usersApi } from '../../api/users';

const { Title } = Typography;

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '사용자 목록을 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: 'OPERATOR' });
    setModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      displayName: user.displayName,
      email: user.email,
      role: user.role,
      status: user.status,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await usersApi.delete(id);
      message.success('사용자가 삭제되었습니다');
      load();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '삭제 실패');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        const payload: any = {
          displayName: values.displayName,
          email: values.email,
          role: values.role,
          status: values.status,
        };
        if (values.password) payload.password = values.password;
        await usersApi.update(editingUser.id, payload);
        message.success('사용자가 수정되었습니다');
      } else {
        await usersApi.create({
          username: values.username,
          password: values.password,
          displayName: values.displayName,
          email: values.email,
          role: values.role,
        });
        message.success('사용자가 추가되었습니다');
      }
      setModalOpen(false);
      load();
    } catch (error: any) {
      if (error?.response?.data?.message) {
        message.error(error.response.data.message);
      }
    }
  };

  const getRoleTag = (role: string) => {
    if (role === 'ADMIN') return <Tag color="red">ADMIN</Tag>;
    if (role === 'OPERATOR') return <Tag color="blue">OPERATOR</Tag>;
    return <Tag>{role}</Tag>;
  };

  const getStatusTag = (status: string) => {
    if (status === 'ACTIVE') return <Tag color="success">활성</Tag>;
    if (status === 'LOCKED') return <Tag color="warning">잠김</Tag>;
    return <Tag color="default">{status}</Tag>;
  };

  const columns: TableColumnsType<User> = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '아이디', dataIndex: 'username', width: 140 },
    { title: '이름', dataIndex: 'displayName', width: 150 },
    { title: '이메일', dataIndex: 'email' },
    { title: '권한', dataIndex: 'role', width: 100, render: getRoleTag },
    { title: '상태', dataIndex: 'status', width: 80, render: getStatusTag },
    {
      title: '마지막 로그인',
      dataIndex: 'lastLoginAt',
      width: 180,
      render: (val) => (val ? new Date(val).toLocaleString('ko-KR') : '-'),
    },
    {
      title: '동작',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="사용자를 삭제하시겠습니까?"
            okText="삭제"
            cancelText="취소"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>사용자 관리</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>새로고침</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>사용자 추가</Button>
        </Space>
      </div>

      <Card>
        <Table<User>
          rowKey="id"
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title={editingUser ? `사용자 수정 (#${editingUser.id})` : '새 사용자 추가'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingUser ? '수정' : '저장'}
        cancelText="취소"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="아이디"
            rules={[
              { required: !editingUser, message: '아이디를 입력하세요' },
              { max: 50 },
            ]}
          >
            <Input disabled={!!editingUser} />
          </Form.Item>
          <Form.Item
            name="password"
            label={editingUser ? '비밀번호 (변경 시에만 입력)' : '비밀번호'}
            rules={[
              { required: !editingUser, message: '비밀번호를 입력하세요' },
              { min: 6, message: '6자 이상 입력하세요' },
            ]}
          >
            <Input.Password placeholder={editingUser ? '비워두면 변경 안 함' : '6자 이상'} />
          </Form.Item>
          <Form.Item name="displayName" label="이름">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="이메일">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="role" label="권한" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'ADMIN', label: 'ADMIN (전체 관리)' },
                { value: 'OPERATOR', label: 'OPERATOR (운영)' },
                { value: 'VIEWER', label: 'VIEWER (조회만)' },
              ]}
            />
          </Form.Item>
          {editingUser && (
            <Form.Item name="status" label="상태" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'ACTIVE', label: '활성' },
                  { value: 'LOCKED', label: '잠김' },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
